/**
 * ReferralResponsePage
 * Public page for third-party clinics to respond to exam referrals via token link.
 *
 * Routes:
 *   /agendamentos/:id/referral/:action/token/:token
 *   action = accept | refuse | reschedule
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Done,
  Error as ErrorIcon,
  MedicalServices,
} from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import apiClient from '../../services/apiClient';

type ReferralAction = 'accept' | 'refuse' | 'reschedule';

const ACTION_LABELS: Record<ReferralAction, { title: string; message: string }> = {
  accept: {
    title: 'Encaminhamento Aceito',
    message: 'Voce aceitou o encaminhamento de exames. O responsavel sera notificado.',
  },
  refuse: {
    title: 'Encaminhamento Recusado',
    message: 'Voce recusou o encaminhamento. O responsavel sera notificado para tomar as providencias necessarias.',
  },
  reschedule: {
    title: 'Reagendamento Solicitado',
    message: 'Sua solicitacao de reagendamento foi registrada. O responsavel entrara em contato.',
  },
};

export const ReferralResponsePage: React.FC = () => {
  const { id, action, token } = useParams<{ id: string; action: string; token: string }>();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const referralAction = action as ReferralAction;
  const actionConfig = ACTION_LABELS[referralAction];

  useEffect(() => {
    if (id && action && token) {
      executeAction();
    } else {
      setError('Link invalido');
      setLoading(false);
    }
  }, [id, action, token]);

  const executeAction = async () => {
    if (!id || !action || !token) {
      setError('Link invalido');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      await apiClient.put(`/appointments/${id}/referral/${action}/token/${token}`);
      setSuccess(true);
    } catch (err: unknown) {
      console.error('Error executing referral action:', err);
      const errorMessage =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        'Erro ao processar sua resposta. O token pode estar expirado ou ja ter sido utilizado.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        bgcolor="background.default"
      >
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ mt: 3 }}>
          Processando...
        </Typography>
      </Box>
    );
  }

  if (success && actionConfig) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        bgcolor="background.default"
        p={3}
      >
        <Paper sx={{ p: 4, maxWidth: 500, textAlign: 'center' }}>
          <Done sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
          <Typography variant="h4" gutterBottom>
            {actionConfig.title}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {actionConfig.message}
          </Typography>
        </Paper>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        bgcolor="background.default"
        p={3}
      >
        <Paper sx={{ p: 4, maxWidth: 500, textAlign: 'center' }}>
          <ErrorIcon sx={{ fontSize: 80, color: 'error.main', mb: 2 }} />
          <Typography variant="h4" gutterBottom color="error">
            Ops!
          </Typography>
          <Typography variant="body1" color="text.secondary" mb={3}>
            {error}
          </Typography>
          <Typography variant="caption" color="text.disabled">
            Se o problema persistir, entre em contato com o responsavel pelo agendamento.
          </Typography>
        </Paper>
      </Box>
    );
  }

  // Fallback
  return (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      minHeight="100vh"
      bgcolor="background.default"
      p={3}
    >
      <Paper sx={{ p: 4, maxWidth: 500, textAlign: 'center' }}>
        <MedicalServices sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
        <Typography variant="h5" gutterBottom>
          Resposta de Encaminhamento
        </Typography>
        <Alert severity="info">
          Utilize os botoes no email para responder ao encaminhamento.
        </Alert>
      </Paper>
    </Box>
  );
};

export default ReferralResponsePage;
